import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useLangFile } from '../../context/LanguageContext';
import React, { Fragment, memo } from 'react';
import { Box, Button, Divider, ListItemIcon, ListItemText, Typography } from '@mui/material';
import Styles from '../FieldOverviewList/FieldOverview.module.less';
import { Close } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { voidFunc } from '../../constants/PropTypeUtils';
import CloudIcon from '@mui/icons-material/Cloud';
import List from '@mui/material/List';
import LayerDrawerHeaderItem from '../LayerDrawer/LayerDrawerHeaderItem';
import FeatureConstants from '../../constants/FeatureConstants';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import HelpItemContainer from '../Help/HelpItemContainer';
import { WEATHER_NETWORK_HELP_ARTICLE } from '../../reducers/HelpReducer';
import { WeatherSensor } from '../../constants/WeatherConstants';

const ENABLED_WEATHER_SENSORS = [
  WeatherSensor.AIR_TEMP,
  WeatherSensor.SOIL_TEMP,
  WeatherSensor.WIND,
  WeatherSensor.WIND_MAX,
  WeatherSensor.RAIN,
  WeatherSensor.HUMIDITY,
  WeatherSensor.PRESSURE,
  WeatherSensor.UV,
];

const WeatherOverview = (props: WeatherOverview.propTypes) => {
  const {
    showWeatherStations,
    showWeatherNetwork,
    displayData,
    onCloseClicked,
    onToggleWeatherStations,
    onToggleWeatherNetwork,
    onToggleWeatherStationSensor,
  } = props;

  const theme = useTheme();
  const LangFile = useLangFile();

  return (
    <Fragment>
      <Box className={Styles.box}>
        <Box
          sx={{
            mx: 1.5,
            mt: 1,
            mb: 0.5,
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Button className={Styles.button} onClick={onCloseClicked}>
              <Close />
            </Button>
            <Box
              sx={{
                mx: 1.5,
              }}
            >
              <Typography>{LangFile.WeatherLayerDrawerContainer.title}</Typography>
            </Box>
            <CloudIcon style={{ color: theme.palette.primary.main }} />
          </Box>
        </Box>
        <Divider />
        <Stack
          direction={'column'}
          spacing={0}
          sx={{
            flexWrap: 'nowrap',
          }}
        >
          <List disablePadding>
            <LayerDrawerHeaderItem
              featureRule={'all'}
              onToggle={onToggleWeatherStations}
              primaryText={LangFile.WeatherLayerList.weather}
              tooltip={LangFile.WeatherLayerList.weatherTooltip}
              requiresFeatures={[FeatureConstants.LEGACY_WEATHER]}
              toggled={Boolean(showWeatherStations)}
            />
            <Collapse in={showWeatherStations} style={{ overflow: 'hidden' }}>
              <List disablePadding>
                {ENABLED_WEATHER_SENSORS.map((sensor) => (
                  <ListItem
                    dense
                    button
                    key={sensor}
                    onClick={onToggleWeatherStationSensor(sensor)}
                  >
                    <ListItemIcon style={{ minWidth: 36 }}>
                      <Checkbox
                        style={{ padding: 0 }}
                        checked={Boolean(displayData[sensor])}
                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                        checkedIcon={<CheckBoxOutlined fontSize="small" />}
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText primary={LangFile.WeatherLayerList.weatherAttrs[sensor]} />
                  </ListItem>
                ))}
                <Divider />
              </List>
            </Collapse>
            <LayerDrawerHeaderItem
              tooltip={LangFile.WeatherNetworkMenu.networkTooltip}
              featureRule={'either'}
              onToggle={onToggleWeatherNetwork}
              primaryText={LangFile.WeatherNetworkMenu.network}
              requiresFeatures={[FeatureConstants.LEGACY_WEATHER]}
              toggled={showWeatherNetwork}
            />
            {showWeatherNetwork && (
              <List dense disablePadding>
                <HelpItemContainer articleId={WEATHER_NETWORK_HELP_ARTICLE} />
              </List>
            )}
          </List>
        </Stack>
      </Box>
    </Fragment>
  );
};

WeatherOverview.propTypes = {
  showWeatherStations: PropTypes.bool,
  showWeatherNetwork: PropTypes.bool,
  displayData: PropTypes.object,

  onCloseClicked: PropTypes.func,
  onToggleWeatherStations: PropTypes.func,
  onToggleWeatherNetwork: PropTypes.func,
  onToggleWeatherStationSensor: PropTypes.func,
};

WeatherOverview.defaultProps = {
  onCloseClicked: voidFunc,
  onToggleWeatherStations: voidFunc,
  onToggleWeatherNetwork: voidFunc,
  onToggleWeatherStationSensor: voidFunc,
};

export default memo(WeatherOverview);
